/**
 * reglagesDeParagraphe — le SEUL endroit qui traduit un réglage de paragraphe
 * demandé par l'agent (`format_paragraphs`, unités d'avocat) en ce que
 * l'éditeur reçoit, et qui dit ce qu'« être conforme » veut dire pour lui
 * (#1866, spec #1858, règles communes n°3 et n°4). Les conversions d'unités
 * vivent dans `unitesDeForme.ts`.
 *
 * Module pur : il ne connaît ni l'éditeur ni le navigateur. L'exécuteur
 * (`formatParagraphsExecutor.ts`) lui demande quoi écrire, la vérification
 * (`verificationDeParagraphe.ts`) lui demande comment juger.
 *
 * ## Cinq écritures, pas dix
 *
 * | écriture | réglages | ce que l'éditeur reçoit |
 * |---|---|---|
 * | style | `styleId` | `styles.paragraph.setStyleRef`, une par paragraphe |
 * | alignement | `alignment` | une étape de plan atomique (`format.apply`, `alignment`) |
 * | retraits | `indentLeftCm`, `indentRightCm`, `indentFirstLineCm` | `format.paragraph.setIndentation`, en twips |
 * | espacement | `spaceBeforePt`, `spaceAfterPt`, `lineSpacing` | `format.paragraph.setSpacing`, en vingtièmes de point et 240ᵉ de ligne |
 * | bordure | `border`, `borderWidthPt` | `format.paragraph.setBorder` par côté à tracer ou à annuler, `clearBorder` par côté à effacer (#1867) |
 * | fond | `shading` | `format.paragraph.setShading`, ou `clearShading` pour l'effacer |
 * | fond de cellule | `cellShading` | `tables.setShading` sur la cellule du paragraphe, ou `tables.clearShading` |
 * | cadre de case | `cellBorder` | `tables.setBorders` en mode `edges` sur la cellule du paragraphe : UNE écriture pour ses quatre côtés |
 *
 * Une copie de forme (`copieDeForme.ts`, #1869) passe par les mêmes écritures :
 * elle demande les valeurs que montre le paragraphe modèle, et pose la bordure
 * par ses traits explicites ({@link ecrituresDesTraits}).
 *
 * ## Ce qui est mesuré, et dont ces règles dépendent
 *
 * Sonde `tests/ws-mocked/mesure-format-paragraphs.spec.ts` (SuperDoc 2.15.1,
 * 2026-10-02, éditeur en mode suggestion, appels en direct ; la lettre et le
 * numéro renvoient au geste de la sonde) :
 *
 * - **Une écriture REMPLACE tout l'élément** (R3 à R7, E1). `setIndentation`
 *   réécrit `w:ind` avec les seuls attributs donnés, `setSpacing` réécrit
 *   `w:spacing` : poser le retrait droit seul efface le retrait gauche posé à
 *   la main. Chaque écriture redonne donc ce que la forme directe du paragraphe
 *   porte déjà (`FormeDeParagraphe.aLaMain`), dans les unités du fichier, et
 *   n'y change que les réglages demandés. Ce qui vient du style n'est pas
 *   redonné : il reste hérité.
 * - **Première ligne et retrait suspendu s'excluent** (Q5 : les deux dans un
 *   appel sont refusés). Une première ligne négative part en `hanging`, et
 *   l'autre attribut n'est pas redonné.
 * - **L'interligne exige sa règle** (E4c : `line` sans `lineRule` est refusé).
 * - **La même valeur reposée est refusée** (R2, E2, E4b : `NO_OP`, rendu, aucune
 *   entrée d'historique). Une valeur que le style donne déjà est, elle, écrite
 *   à la main sans refus (Q3) — pour une entrée d'historique de plus.
 * - **L'alignement en plan ne refuse rien** (A1 à A5) : une étape qui ne change
 *   rien est acceptée, sans entrée d'historique ; un paragraphe sans texte
 *   s'aligne comme un autre.
 *
 * - **Les attributs que l'éditeur ne nomme pas sont perdus** (S1 à S6) :
 *   `w:beforeAutospacing`, `w:beforeLines`, `w:leftChars`… sont refusés en
 *   entrée (`INVALID_INPUT`) et effacés par toute écriture du même élément —
 *   l'« avant » peint d'un paragraphe à espacement automatique passe de 13,8 à
 *   6 pt quand on écrit son « après ». L'écriture qui toucherait un élément
 *   porteur de tels attributs n'est donc PAS faite (`ecrituresEmpechees`) :
 *   les réglages qu'elle portait restent tels quels sur ce paragraphe, comptés
 *   et nommés par le reçu. L'alignement, qui n'y touche pas (S8), et l'autre
 *   écriture si son élément est sain (S9), sont faits. `w:contextualSpacing`,
 *   élément à part, survit (S7). Un espacement automatique ÉTEINT se perd sans
 *   rien changer à l'écran (Z1) : le lecteur ne le compte pas.
 *
 * ## La bordure (#1867)
 *
 * Sonde `tests/ws-mocked/mesure-format-paragraphs-bordure-reset.spec.ts`
 * (SuperDoc 2.15.1, 2026-10-02, éditeur en mode suggestion, appels en direct) :
 *
 * - **Une écriture par CÔTÉ, et elle ne touche que ce côté** (C1, C3) :
 *   `setBorder` garde les autres côtés de `w:pBdr`, et `w:spacing`, `w:ind`,
 *   `w:jc` voisins. Rien n'est donc à redonner, au contraire des retraits.
 * - **Aucune voie ne pose plusieurs côtés à la fois** (V2 à V4) : un côté
 *   `all` ou `box` est refusé (`INVALID_INPUT`), une étape de plan aussi
 *   (`CAPABILITY_UNSUPPORTED`), et `format.apply` n'a pas de bordure. Un
 *   encadré coûte quatre écritures par paragraphe.
 * - **L'éditeur écrit ce qu'on lui donne, sans défaut ni contrôle** (C2, C5,
 *   C6, E1) : `size` va tel quel dans `w:sz` (huitièmes de point, entier exigé),
 *   `space` dans `w:space` (points), `color` dans `w:color` — `#` compris, ce
 *   qui n'est pas une couleur du format : la couleur part donc sans lui.
 * - **La même bordure reposée est refusée** (C4, `NO_OP` rendu, aucune entrée
 *   d'historique).
 * - **`clearBorder` n'efface que ce qui est posé à la main** (N1 à N4) : un
 *   côté, ou tout `w:pBdr` (`side:'all'`, une écriture) ; sur un côté porté par
 *   le STYLE, il est refusé (`NO_OP`) et le trait reste peint. Contre un style,
 *   seul un côté annulé à la main retire le trait : `setBorder` de style
 *   `none` écrit `w:left w:val="none"`, que l'éditeur ne peint pas (N5).
 * - **Aucune de ces écritures ne crée ni n'efface de révision** (C1 à N6 : 0 ;
 *   M2, M4 : une révision en attente sur le paragraphe est gardée).
 * - **La page peint la bordure** dans un élément du paragraphe
 *   (`.superdoc-paragraph-border`), au pixel entier inférieur et jamais moins
 *   d'un : 2,25 pt → 3 px, 1,25 pt → 1 px, 0,5 pt → 1 px. Deux voisins aux `w:pBdr` identiques sont peints comme UN encadré —
 *   le premier sans trait du bas, le second sans trait du haut (P1) —, ce que
 *   la norme prévoit aussi (ISO/IEC 29500-1, élément `pBdr` : « The resulting
 *   paragraphs have identical pBdr values, therefore they would use the top,
 *   left, bottom, and right borders around them as a unit »,
 *   https://learn.microsoft.com/en-us/dotnet/api/documentformat.openxml.wordprocessing.paragraphborders,
 *   vérifié 2026-10-02). Le lecteur ne le dit pas : il lit chaque paragraphe seul.
 *
 * `border` dit TOUT l'état, dans les mots de la lecture (`cotesDeLaBordure`,
 * contrat partagé) : les côtés nommés sont tracés, les autres retirés. Trois
 * cas, par ce que l'avocat demande :
 *
 * - `left` seul : la barre de marge d'Hector ({@link barreDeMarge}) ;
 * - `box` : les quatre côtés à la MÊME épaisseur et aux écarts de l'encadré.
 *   L'état demandé comprend l'écart au texte lu dans le fichier (une barre de
 *   marge à 14 pt du texte, même de l'épaisseur demandée, est réécrite à
 *   l'écart de l'encadré : sinon le cadre serait asymétrique) ;
 * - tout autre choix de côtés (`bottom`, `top+bottom`, `left+bottom`…) : un
 *   trait simple noir sur chacun, à l'écart que l'encadré donne à ce côté. Un
 *   côté déjà tracé à l'épaisseur voulue n'est pas réécrit, quel que soit son
 *   écart : il n'y a pas de cadre à tenir symétrique. Mesuré le 2026-10-11
 *   (`tests/ws-mocked/mesure-traits-et-fonds.spec.ts`, T1 à T8) : le bas, le
 *   haut et la droite s'écrivent et s'effacent comme la gauche, un côté à la
 *   fois, dans le corps, dans une cellule et dans un en-tête.
 *
 * Un côté déjà dans l'état demandé n'est pas réécrit. Sans `borderWidthPt`, un
 * trait déjà là garde son épaisseur, et un encadré dont les quatre côtés ont
 * déjà la même la garde ; sinon l'encadré prend l'épaisseur par défaut sur ses
 * quatre côtés, et un côté à tracer celle de l'encadré.
 *
 * ## Le fond (2026-10-11)
 *
 * Sonde `tests/ws-mocked/mesure-traits-et-fonds.spec.ts` (SuperDoc 2.15.1,
 * 2026-10-11, éditeur en mode suggestion, appels en direct) :
 *
 * - **Fond de paragraphe** : `format.paragraph.setShading` REMPLACE `w:shd`
 *   par les seuls attributs donnés (F7, F16), et écrit `fill` tel quel, `#` et
 *   minuscules compris (F3). Il reçoit donc toujours les trois attributs que
 *   Word écrit — `fill`, `color:'auto'`, `pattern:'clear'` (F15 :
 *   `<w:shd w:color="auto" w:fill="…" w:val="clear"/>`) — et une couleur
 *   normalisée par le contrat. Il ne touche ni `w:pBdr`, ni `w:spacing`,
 *   `w:ind`, `w:jc` (F6, F7). La même valeur reposée est refusée (`NO_OP`, F2).
 * - **Le retirer** : `clearShading` n'efface que le fond posé à la main (F4,
 *   F8) ; contre le fond d'un STYLE il est refusé (`NO_OP`, F9) et le fond
 *   reste peint. Contre un style, `setShading` de `fill:'auto'` écrit
 *   `w:shd w:fill="auto"`, que l'éditeur ne peint pas (F10) — le pendant du
 *   côté de bordure annulé à la main.
 * - **Fond de cellule** : `tables.setShading({ nodeId: 'tc:<premier
 *   paragraphe>', color })` écrit `w:tcPr/w:shd w:val="clear" w:fill` (C2), et
 *   normalise lui-même la couleur (C5). `tables.clearShading` l'efface (C8).
 *   La cellule ne se trouve ni par un autre de ses paragraphes, ni par
 *   l'identifiant nu du paragraphe (C7 : l'appel JETTE).
 *   ⚠️ **La même valeur reposée, et l'effacement d'une cellule sans fond,
 *   rendent `success:true` sans entrée d'historique ni avance de révision**
 *   (C3, C9) : une telle écriture serait comptée sans rien avoir écrit. Elle
 *   n'est donc soumise que si le fichier montre qu'elle change la cellule.
 * - Aucune de ces écritures ne laisse de révision en mode direct (F1 à C9 : 0) ;
 *   chacune qui change le fichier laisse une entrée d'historique, que
 *   l'annulation défait (F14, C12). Aucune n'a d'étape de plan (F12).
 * - **Non mesuré** : un fond de cellule que donne un STYLE DE TABLEAU (le
 *   lecteur le rend non lisible ; le retirer y pose `fill:'auto'`, comme pour
 *   un paragraphe) ; un fond à motif (`w:val` autre que `clear`) ; un fond posé
 *   sur un paragraphe dont la forme attend une décision (`w:pPrChange`).
 *
 * ## Le cadre d'une case de tableau (2026-10-12)
 *
 * Sonde `tests/ws-mocked/mesure-bordures-de-case.spec.ts` (SuperDoc 2.15.1,
 * 2026-10-12, éditeur en mode suggestion, appels en direct ; relevé au §22 de
 * `superdoc-document-api.md`). Cas de départ : le titre qu'Hector écrit est
 * l'unique case d'un tableau dont `w:tblBorders` trace six traits.
 *
 * - **`tables.setBorders({ nodeId: 'tc:<premier paragraphe>', mode: 'edges',
 *   edges })` écrit les quatre côtés d'une case en UNE écriture** (E1, E3) :
 *   `null` écrit `w:tcBorders/w:top w:val="nil"`, un trait
 *   `{ lineStyle, lineWeightPt, color }` écrit `w:val`, `w:sz`, `w:color` ; un
 *   côté absent de `edges` n'est pas touché. `tables.clearBorder` et
 *   `tables.setBorder` font de même un côté à la fois (C1 à C5).
 * - **Un côté annulé sur la case n'est plus peint, d'où que vienne le trait** :
 *   de `w:tblBorders` (C1 à C4, E1), d'un style de tableau (S1, S2), de la case
 *   elle-même (D1 : `nil` remplace le trait). Effacer au niveau du TABLEAU ne
 *   retire pas un trait que la case pose (G8) : la case est le seul niveau qui
 *   décide toujours.
 * - ⚠️ **Les mêmes côtés reposés rendent `success:true` sans entrée
 *   d'historique ni avance de révision** (C2, C5, E2) — le piège du fond de
 *   cellule. L'écriture ne porte donc que les côtés que le fichier montre à
 *   changer, et n'est pas soumise quand il n'y en a pas.
 * - ⚠️ **L'éditeur écrit le côté qu'on lui nomme, sans contrôle** : `edge:'all'`
 *   écrit un élément `<w:all>` que le format ne connaît pas (C6d), une
 *   épaisseur nulle écrit `w:sz="1"` (C6a). Seuls les quatre côtés du contrat
 *   partent, avec un trait fixe.
 * - La case ne se trouve ni par un autre de ses paragraphes ni par
 *   l'identifiant nu (C8, E8 : l'appel JETTE) ; aucune étape de plan (C13,
 *   E9) ; 0 révision en direct, une entrée d'historique que l'annulation défait
 *   (E5) ; `REVISION_MISMATCH` rendu sous une révision périmée (E8).
 * - ⚠️ **Un côté PARTAGÉ reste peint tant que la case voisine le trace** (C9,
 *   C10) : retirer le cadre d'une case d'un tableau à plusieurs cases ne retire
 *   à l'écran que ses côtés de bord, sauf à viser aussi ses voisines.
 * - **Non mesuré** : ce que Word peint pour un côté annulé sur une case et
 *   tracé par sa voisine ; un tableau à cases fusionnées ; le tableau d'un
 *   en-tête ; une page figée par un plan refusé.
 *
 * `cellBorder` dit TOUT le cadre de la case, dans les mots de `border`
 * (`cotesDeLaBordure`) : un côté nommé que le fichier montre déjà tracé garde
 * son trait, un côté nommé sans trait reçoit un trait simple noir d'un
 * demi-point (celui de l'encadré), tout autre côté tracé est annulé.
 *
 * ## Le style nommé
 *
 * Sondes `tests/ws-mocked/mesure-copy-format.spec.ts` (S1 à S7, 2026-10-04) et
 * `mesure-socle-styles-nommes.spec.ts` (b, 2026-10-05), SuperDoc 2.15.1 :
 *
 * - **`setStyleRef` ne change que `w:pStyle`** : ce que le paragraphe et son
 *   texte posent à la main reste (un mot en gras, un alignement). `setStyle`
 *   efface la forme directe du texte : il n'est pas employé.
 * - **Un style que le fichier ne définit pas est refusé** (`INVALID_INPUT`,
 *   rendu) ; **le même style reposé est accepté** et coûte une entrée
 *   d'historique : un paragraphe qui porte déjà le style n'est pas écrit.
 *   Un paragraphe sans `w:pStyle` porte le style par défaut du fichier : le
 *   lui demander n'écrit rien, et il est dit conforme — l'avocat le voit déjà.
 * - **Aucun lot** : une cible en tableau ou au pluriel est refusée
 *   (`INVALID_TARGET`), une étape de plan aussi (`CAPABILITY_UNSUPPORTED`).
 *   Une écriture par paragraphe, 304 ms pour six.
 * - **Un style qui numérote numérote le paragraphe qui le reçoit** (S7) ; la
 *   numérotation posée à la main survit (S6).
 *
 * Un style demandé avec d'autres réglages : ceux-ci sont posés À LA MAIN
 * par-dessus lui, et se décident donc sur ce que le paragraphe montrera une
 * fois le style posé ({@link sousLeStyle}), pas sur ce qu'il montre avant —
 * le justifié qu'il tient de son style actuel part avec ce style.
 *
 * ## « Rien à écrire »
 *
 * Un réglage que le paragraphe montre déjà — posé à la main, porté par son
 * style ou par les défauts — n'est pas réécrit : l'écran ne changerait pas, et
 * chaque écriture par paragraphe coûte une des cent entrées de l'historique.
 * Un réglage qui ne se lit pas est écrit : rien ne prouve qu'il est en place.
 */

import {
  CANVAS_PARAGRAPH_FORMAT_KEYS,
  type CanvasFormatSettingValue,
  type CanvasParagraphAlignment,
  type CanvasParagraphFormatKey,
  type CanvasParagraphSettingKey,
} from '@hector/shared-types';
import {
  CANVAS_CELL_BORDER_SETTING_KEY,
  CANVAS_PARAGRAPH_BORDER_SIDES,
  CANVAS_SHADING_NONE,
  CANVAS_SHADING_SETTING_KEYS,
  cotesDeLaBordure,
  fondNormalise,
  type CanvasParagraphBorderSide,
  type CanvasShadingSettingKey,
} from '@hector/shared-types';
import { getSpec } from '../../styleSpec';
import { cmEnTwips, multipleEnInterligne, ptEnHuitiemes, ptEnVingtiemes, resumerLaBordure, TOLERANCE_DE_REGLAGE, type FormeDeParagraphe, type FormeDirecteDeParagraphe, type Reglage, type StyleDeParagraphe, type TraitDeBordure } from '@hector/docx-forme';

/**
 * Les réglages de paragraphe demandés par l'agent, normalisés ({@link reglagesDeParagrapheDemandes}) : le style nommé
 * par son identifiant, les fonds sous la forme que rend la lecture (`fondNormalise`), les autres — le cadre de case
 * compris — par leur valeur.
 */
export type ReglagesDeParagraphe = { styleId?: string } & Partial<Record<CanvasShadingSettingKey | typeof CANVAS_CELL_BORDER_SETTING_KEY, string>> & Partial<Record<CanvasParagraphFormatKey, CanvasFormatSettingValue>>;

/**
 * Ce qui est demandé pour les paragraphes d'une cible : les réglages, et le
 * style nommé que `reglages.styleId` désigne, tel que le fichier le définit.
 * Bâtie par {@link demandeDeParagraphes} seule : `style` est présent si et
 * seulement si un style est demandé, et c'est celui-là.
 */
export interface DemandeDeParagraphes {
  readonly reglages: ReglagesDeParagraphe;
  readonly style?: StyleDeParagraphe;
}

/** Ce que la relecture dit d'un paragraphe pour un réglage demandé. */
export type VerdictDeParagraphe =
  | { issue: 'conforme' }
  | { issue: 'different'; lu: CanvasFormatSettingValue }
  | { issue: 'non_lisible' };

/** Les écritures qu'un paragraphe peut recevoir ; `fondDeCellule` et `cadreDeCase` s'écrivent sur la cellule de tableau qui le contient. */
export type FamilleDEcriture = 'style' | 'alignement' | 'retraits' | 'espacement' | 'bordure' | 'fond' | 'fondDeCellule' | 'cadreDeCase';

/** Les écritures qui remplacent tout un élément du paragraphe, et peuvent donc y effacer ce qu'on ne redonne pas. */
type FamilleQuiRemplace = 'retraits' | 'espacement';

/**
 * Les types de trait que l'éditeur écrit tels quels, mesurés
 * (`mesure-format-paragraphs-bordure-reset`, C1 ; `mesure-copy-format`, B1 et
 * B2, 2026-10-04). Un autre type n'est pas envoyé à l'éditeur.
 */
export const TYPES_DE_TRAIT_MESURES = ['single', 'double', 'dotted', 'dashed'] as const;
/** Un type de trait de bordure que l'éditeur écrit tel quel. */
export type TypeDeTraitMesure = (typeof TYPES_DE_TRAIT_MESURES)[number];
/** Un trait dont le type est de ceux que l'éditeur écrit tels quels. */
export type TraitMesure = TraitDeBordure & { type: TypeDeTraitMesure };
/** Vrai si le type du trait est mesuré : il peut être écrit. */
export const estTraitMesure = (trait: TraitDeBordure): trait is TraitMesure => TYPES_DE_TRAIT_MESURES.some((type) => type === trait.type);

/**
 * Une écriture de bordure, sur UN côté : un trait posé (`single` pour une
 * bordure demandée, le type du trait du modèle pour une copie de forme) ou
 * annulé contre celui d'un style (`none`) par `format.paragraph.setBorder`, ou
 * la forme directe effacée par `format.paragraph.clearBorder` (`all` : tout
 * `w:pBdr`).
 */
export type EcritureDeBordure =
  | { geste: 'poser'; side: CanvasParagraphBorderSide; style: TypeDeTraitMesure; size: number; space: number; color: string }
  | { geste: 'poser'; side: CanvasParagraphBorderSide; style: 'none' }
  | { geste: 'effacer'; side: CanvasParagraphBorderSide | 'all' };

/**
 * Une écriture de fond : une couleur posée (`RRGGBB`, ou `auto` pour annuler à
 * la main le fond que donne un style), ou le fond posé à la main effacé.
 */
export type EcritureDeFond = { geste: 'poser'; couleur: string } | { geste: 'effacer' };

/** Ce que l'éditeur doit recevoir pour UN paragraphe ; une écriture absente n'y changerait rien. */
export interface EcrituresDeParagraphe {
  /** L'identifiant du style nommé à poser (`styles.paragraph.setStyleRef`). */
  style?: string;
  /** L'alignement de son étape de plan. */
  alignement?: CanvasParagraphAlignment;
  /** L'entrée de `format.paragraph.setIndentation`, en twips : tous les retraits que la forme directe doit porter après l'écriture. */
  retraits?: FormeDirecteDeParagraphe['retraits'];
  /** L'entrée de `format.paragraph.setSpacing`, en unités du fichier : tout l'espacement que la forme directe doit porter après l'écriture. */
  espacement?: FormeDirecteDeParagraphe['espacement'];
  /** Les écritures de bordure, une par côté à changer, dans l'ordre où les soumettre. Jamais vide. */
  bordure?: EcritureDeBordure[];
  /** Le fond du paragraphe (`format.paragraph.setShading` / `clearShading`). */
  fond?: EcritureDeFond;
  /** Le fond de la cellule du paragraphe (`tables.setShading` / `clearShading`), et l'identifiant sous lequel l'éditeur la trouve (`tc:<premier paragraphe>`). */
  fondDeCellule?: EcritureDeFond & { cellule: string };
  /**
   * Les côtés à changer du cadre de la cellule du paragraphe (`tables.setBorders`, mode `edges`) : un trait à tracer,
   * `null` pour un côté à annuler. Jamais vide ; un côté absent est déjà dans l'état demandé.
   */
  cadreDeCase?: { cellule: string; cotes: Partial<Record<CanvasParagraphBorderSide, TraitDeCase | null>> };
}

/** Le trait qu'on trace sur un côté d'une case, tel que `tables.setBorders` le reçoit. */
export interface TraitDeCase { lineStyle: 'single'; lineWeightPt: number; color: string }

/**
 * La barre de marge d'Hector (`styleSpec.ts`, `Hector-Ligne-Marge`) : seule
 * source de son épaisseur, de son écart au texte et de sa couleur. Word ne
 * réunit deux barres voisines en un trait continu que si leurs `w:pBdr` sont
 * identiques : un trait posé par l'agent doit être celui que pose la
 * génération d'acte.
 */
function barreDeMarge(): { width_pt: number; space_pt: number; color: string } {
  const barre = getSpec('Hector-Ligne-Marge').leftBorder;
  if (barre === undefined) {
    throw new Error(
      'format_paragraphs border: the style spec "Hector-Ligne-Marge" (features/redaction/styleSpec.ts) has no leftBorder, '
      + 'which defines the margin bar (width, space, colour). Restore leftBorder on that spec.',
    );
  }
  return barre;
}

/**
 * L'encadré : son épaisseur quand aucune n'est demandée, l'écart entre chaque
 * trait et le texte, sa couleur. Valeurs de jugement, non mesurées : un
 * demi-point, à 1 pt du texte en haut et en bas et 4 pt sur les côtés, noir.
 */
const ENCADRE_PAR_DEFAUT: { epaisseurPt: number; ecartPt: Readonly<Record<CanvasParagraphBorderSide, number>>; couleur: string } = {
  epaisseurPt: 0.5,
  ecartPt: { top: 1, left: 4, bottom: 1, right: 4 },
  couleur: '000000',
};

/** Vrai si les quatre côtés du paragraphe sont tracés à la même épaisseur. */
function encadreUniforme(paragraphe: FormeDeParagraphe): boolean {
  const epaisseurs = CANVAS_PARAGRAPH_BORDER_SIDES.map((cote) => paragraphe.bordures[cote]);
  const premiere = epaisseurs[0];
  return premiere !== undefined && premiere.lisible && premiere.valeur > 0
    && epaisseurs.every((lu) => lu.lisible && lu.valeur === premiere.valeur);
}

/**
 * Les écritures qui amènent la bordure d'un paragraphe à l'état demandé,
 * d'après ses côtés lus AVANT l'écriture. Vide : elle y est déjà.
 *
 * Un côté qui ne se lit pas est écrit : rien ne prouve qu'il est dans l'état
 * demandé.
 */
function ecrituresDeBordure(paragraphe: FormeDeParagraphe, aTracer: readonly CanvasParagraphBorderSide[], epaisseurPt: number | undefined): EcritureDeBordure[] {
  // Les trois cas de l'en-tête (« La bordure ») : barre de marge, encadré, côtés choisis.
  const sorte = aTracer.length === 1 && aTracer[0] === 'left' ? 'barre' : aTracer.length === CANVAS_PARAGRAPH_BORDER_SIDES.length ? 'encadre' : 'cotes';
  const ecritures: EcritureDeBordure[] = [];
  const aRetirer: CanvasParagraphBorderSide[] = [];
  // L'épaisseur que chaque côté tracé doit montrer ; `undefined` : celle qu'il a déjà convient.
  const epaisseurVoulue = epaisseurPt ?? (sorte === 'encadre' && !encadreUniforme(paragraphe) ? ENCADRE_PAR_DEFAUT.epaisseurPt : undefined);
  for (const cote of CANVAS_PARAGRAPH_BORDER_SIDES) {
    const lu = paragraphe.bordures[cote];
    if (aTracer.includes(cote)) {
      const epaisseurEnPlace = lu.lisible && lu.valeur > 0 && (epaisseurVoulue === undefined || Math.abs(lu.valeur - epaisseurVoulue) <= TOLERANCE_DE_REGLAGE.borderWidthPt);
      if (sorte === 'barre') {
        if (epaisseurEnPlace) continue;
        // La barre de marge n'est lue que pour le trait à gauche seul : les autres bordures n'en dépendent pas.
        const barre = barreDeMarge();
        ecritures.push({ geste: 'poser', side: cote, style: 'single', size: ptEnHuitiemes(epaisseurVoulue ?? barre.width_pt), space: barre.space_pt, color: barre.color });
        continue;
      }
      // Un encadré a aussi SES écarts : un côté à la bonne épaisseur mais à un autre écart (ou à l'écart non lisible) est réécrit.
      const ecart = paragraphe.ecartsDeBordure[cote];
      if (epaisseurEnPlace && (sorte === 'cotes' || (ecart.lisible && ecart.valeur === ENCADRE_PAR_DEFAUT.ecartPt[cote]))) continue;
      const epaisseur = epaisseurVoulue ?? (lu.lisible && lu.valeur > 0 ? lu.valeur : ENCADRE_PAR_DEFAUT.epaisseurPt);
      ecritures.push({ geste: 'poser', side: cote, style: 'single', size: ptEnHuitiemes(epaisseur), space: ENCADRE_PAR_DEFAUT.ecartPt[cote], color: ENCADRE_PAR_DEFAUT.couleur });
    } else if (!(lu.lisible && lu.valeur === 0)) {
      aRetirer.push(cote);
    }
  }
  const rienDessous = (cote: CanvasParagraphBorderSide): boolean => {
    const dessous = paragraphe.borduresSousLaMain[cote];
    return dessous.lisible && dessous.valeur === 0;
  };
  // Tout retirer d'un paragraphe dont le style ne trace rien : une seule écriture efface `w:pBdr`.
  if (aTracer.length === 0 && aRetirer.length > 0 && CANVAS_PARAGRAPH_BORDER_SIDES.every(rienDessous)) return [{ geste: 'effacer', side: 'all' }];
  for (const cote of aRetirer) {
    // Effacer suffit quand le trait est posé à la main sur un style qui n'en trace pas ; sinon le côté est annulé à la main.
    const aLaMain = paragraphe.formeDirecte.cotesDeBordure.includes(cote);
    ecritures.push(aLaMain && rienDessous(cote) ? { geste: 'effacer', side: cote } : { geste: 'poser', side: cote, style: 'none' });
  }
  return ecritures;
}

/** Vrai si deux traits s'écrivent de la même façon au fichier ; la couleur ne tient pas compte de la casse. */
export const memeTrait = (a: TraitDeBordure, b: TraitDeBordure): boolean => (
  a.type === b.type && a.huitiemes === b.huitiemes && a.ecart === b.ecart && a.couleur.toUpperCase() === b.couleur.toUpperCase()
);

/**
 * Les écritures qui amènent chaque côté de la bordure d'un paragraphe au trait
 * `voulu` — son type, son épaisseur, son écart et sa couleur, tels qu'un autre
 * paragraphe les porte (copie de forme, #1869). Vide : elle y est déjà.
 *
 * Seuls les types de trait mesurés s'écrivent ({@link TYPES_DE_TRAIT_MESURES} ;
 * couleur `auto` comprise, B2). Les côtés
 * `between` et `bar` ne sont ni lus ni écrits ici : `w:pBdr` n'est donc effacé
 * d'un coup (`all`) que si le paragraphe ne porte à la main aucun autre côté
 * que les quatre.
 *
 * @param paragraphe Le paragraphe à écrire, tel que lu AVANT l'écriture.
 * @param dessous Ce que la cascade donnera à chaque côté sous la forme directe
 *   au moment de l'écriture — celle d'un autre style quand il vient d'être posé.
 */
export function ecrituresDesTraits(
  paragraphe: Pick<FormeDeParagraphe, 'traitsDeBordure' | 'formeDirecte'>,
  dessous: FormeDeParagraphe['traitsDeBordureSousLaMain'],
  voulus: Readonly<Record<CanvasParagraphBorderSide, TraitMesure | null>>,
): EcritureDeBordure[] {
  const aLaMain = paragraphe.formeDirecte.cotesDeBordure;
  const ecritures: EcritureDeBordure[] = [];
  const aRetirer: CanvasParagraphBorderSide[] = [];
  for (const cote of CANVAS_PARAGRAPH_BORDER_SIDES) {
    const [voulu, lu] = [voulus[cote], paragraphe.traitsDeBordure[cote]];
    // Le trait en place : celui de la forme directe quand elle le pose, sinon celui que donne la cascade dessous.
    // Un côté qui ne se lit pas est écrit : rien ne prouve qu'il est dans l'état voulu.
    const enPlace = !lu.lisible || lu.origine === 'direct' ? lu : dessous[cote];
    if (voulu === null) {
      if (!(enPlace.lisible && enPlace.valeur === null)) aRetirer.push(cote);
    } else if (!(enPlace.lisible && enPlace.valeur !== null && memeTrait(enPlace.valeur, voulu))) {
      ecritures.push({ geste: 'poser', side: cote, style: voulu.type, size: voulu.huitiemes, space: voulu.ecart, color: voulu.couleur });
    }
  }
  const rienDessous = (cote: CanvasParagraphBorderSide): boolean => {
    const lu = dessous[cote];
    return lu.lisible && lu.valeur === null;
  };
  const quatreCotesSeuls = aLaMain.every((cote) => CANVAS_PARAGRAPH_BORDER_SIDES.some((connu) => connu === cote));
  if (aRetirer.length === CANVAS_PARAGRAPH_BORDER_SIDES.length && quatreCotesSeuls && CANVAS_PARAGRAPH_BORDER_SIDES.every(rienDessous)) {
    return [{ geste: 'effacer', side: 'all' }];
  }
  for (const cote of aRetirer) {
    // Effacer suffit quand le trait est posé à la main et que rien ne reparaît dessous ; sinon le côté est annulé à la main.
    ecritures.push(aLaMain.includes(cote) && rienDessous(cote) ? { geste: 'effacer', side: cote } : { geste: 'poser', side: cote, style: 'none' });
  }
  return ecritures;
}

const ALIGNEMENTS: readonly CanvasParagraphAlignment[] = ['left', 'center', 'right', 'justify'];

/**
 * Juge ce que le fichier porte, pour un paragraphe, au regard de la demande :
 * la valeur résolue, quelle que soit son origine. Un nombre est conforme à la
 * tolérance de son unité (`unitesDeForme.ts`).
 */
export function jugerLeReglageDeParagraphe(
  setting: CanvasParagraphFormatKey,
  demande: CanvasFormatSettingValue,
  lu: Reglage<CanvasFormatSettingValue>,
): VerdictDeParagraphe {
  if (!lu.lisible) return { issue: 'non_lisible' };
  const conforme = setting !== 'alignment' && setting !== 'border' && typeof demande === 'number' && typeof lu.valeur === 'number'
    ? Math.abs(lu.valeur - demande) <= TOLERANCE_DE_REGLAGE[setting]
    : lu.valeur === demande;
  return conforme ? { issue: 'conforme' } : { issue: 'different', lu: lu.valeur };
}

/**
 * Le paragraphe tel que le fichier le montrera une fois le style `pose` posé,
 * avant toute autre écriture : ce qu'il pose à la main garde sa valeur, le
 * reste prend celle que le style donne. Seul ce que la décision d'écriture lit
 * est prévu — réglages de paragraphe et côtés de bordure ; l'identité, le
 * texte et l'inventaire de la forme directe restent ceux du paragraphe lu.
 *
 * Non prévu, donc écrit s'il est demandé : un réglage qui ne se lit pas, et
 * tout réglage non posé à la main d'un paragraphe numéroté à la main — son
 * niveau de liste passe devant le style, et ce qu'il donne reste.
 */
export function sousLeStyle(paragraphe: FormeDeParagraphe, pose: FormeDeParagraphe): FormeDeParagraphe {
  const sousUneListe = paragraphe.numerotation === 'a_la_main';
  const prevoir = <Valeur>(lu: Reglage<Valeur>, duStyle: Reglage<Valeur>): Reglage<Valeur> => {
    if (!lu.lisible || lu.origine === 'direct') return lu;
    return sousUneListe ? { lisible: false, raison: 'numerotation' } : duStyle;
  };
  const cotes = <Valeur>(lus: Record<CanvasParagraphBorderSide, Reglage<Valeur>>, duStyle: Record<CanvasParagraphBorderSide, Reglage<Valeur>>): Record<CanvasParagraphBorderSide, Reglage<Valeur>> => ({
    top: prevoir(lus.top, duStyle.top),
    left: prevoir(lus.left, duStyle.left),
    bottom: prevoir(lus.bottom, duStyle.bottom),
    right: prevoir(lus.right, duStyle.right),
  });
  /** Sous la forme directe, tout vient du style posé. */
  const dessous = <Valeur>(duStyle: Record<CanvasParagraphBorderSide, Reglage<Valeur>>): Record<CanvasParagraphBorderSide, Reglage<Valeur>> => cotes(duStyle, duStyle);
  const bordures = cotes(paragraphe.bordures, pose.bordures);
  return {
    ...paragraphe,
    paragraphe: {
      alignment: prevoir(paragraphe.paragraphe.alignment, pose.paragraphe.alignment),
      indentLeftCm: prevoir(paragraphe.paragraphe.indentLeftCm, pose.paragraphe.indentLeftCm),
      indentRightCm: prevoir(paragraphe.paragraphe.indentRightCm, pose.paragraphe.indentRightCm),
      indentFirstLineCm: prevoir(paragraphe.paragraphe.indentFirstLineCm, pose.paragraphe.indentFirstLineCm),
      spaceBeforePt: prevoir(paragraphe.paragraphe.spaceBeforePt, pose.paragraphe.spaceBeforePt),
      spaceAfterPt: prevoir(paragraphe.paragraphe.spaceAfterPt, pose.paragraphe.spaceAfterPt),
      lineSpacing: prevoir(paragraphe.paragraphe.lineSpacing, pose.paragraphe.lineSpacing),
      ...resumerLaBordure(bordures),
    },
    bordures,
    ecartsDeBordure: cotes(paragraphe.ecartsDeBordure, pose.ecartsDeBordure),
    borduresSousLaMain: dessous(pose.bordures),
    traitsDeBordure: cotes(paragraphe.traitsDeBordure, pose.traitsDeBordure),
    traitsDeBordureSousLaMain: dessous(pose.traitsDeBordure),
    fond: prevoir(paragraphe.fond, pose.fond),
    fondSousLaMain: pose.fond,
  };
}

/**
 * L'écriture qui amène un fond à l'état demandé, d'après le fond lu AVANT
 * l'écriture ; `undefined` : il y est déjà. Un fond qui ne se lit pas est
 * écrit : rien ne prouve qu'il est dans l'état demandé.
 *
 * @param demande Le fond demandé, sous la forme du contrat (`fondNormalise`).
 * @param aLaMain Vrai si l'élément porte un fond posé à la main, quelle qu'en soit la valeur.
 * @param rienDessous Vrai si, ce fond posé à la main une fois effacé, il est établi qu'aucun fond ne reparaît.
 */
function ecritureDeFond(demande: string, lu: Reglage<string>, aLaMain: boolean, rienDessous: boolean): EcritureDeFond | undefined {
  if (lu.lisible && lu.valeur === demande) return undefined;
  if (demande !== CANVAS_SHADING_NONE) return { geste: 'poser', couleur: demande };
  // Effacer suffit quand le fond est posé à la main et que rien ne reparaît dessous ; sinon il est annulé à la main.
  return aLaMain && rienDessous ? { geste: 'effacer' } : { geste: 'poser', couleur: 'auto' };
}

/**
 * Ce que l'éditeur doit recevoir pour poser À LA MAIN les réglages de
 * `demandes` sur un paragraphe, d'après la forme sur laquelle décider : celle
 * lue AVANT l'écriture, ou celle prévue sous un style à poser d'abord
 * ({@link ecrituresDeLaDemande}, `copieDeForme.ts`). Un objet vide : le
 * paragraphe montre déjà tout ce qui est demandé. Le style nommé n'est pas
 * décidé ici.
 */
export function ecrituresDuParagraphe(paragraphe: FormeDeParagraphe, demandes: ReglagesDeParagraphe): EcrituresDeParagraphe {
  /** La valeur à écrire pour un réglage numérique ; `undefined` s'il n'est pas demandé ou déjà en place. */
  const aEcrire = (setting: Exclude<CanvasParagraphFormatKey, 'alignment' | 'border'>): number | undefined => {
    const demande = demandes[setting];
    if (typeof demande !== 'number') return undefined;
    return jugerLeReglageDeParagraphe(setting, demande, paragraphe.paragraphe[setting]).issue === 'conforme' ? undefined : demande;
  };
  const ecritures: EcrituresDeParagraphe = {};

  const alignement = ALIGNEMENTS.find((connu) => connu === demandes.alignment);
  if (alignement !== undefined && jugerLeReglageDeParagraphe('alignment', alignement, paragraphe.paragraphe.alignment).issue !== 'conforme') {
    ecritures.alignement = alignement;
  }

  const [gauche, droit, premiereLigne] = [aEcrire('indentLeftCm'), aEcrire('indentRightCm'), aEcrire('indentFirstLineCm')];
  if (gauche !== undefined || droit !== undefined || premiereLigne !== undefined) {
    const { firstLine, hanging, ...cotes } = paragraphe.aLaMain.retraits;
    ecritures.retraits = {
      ...cotes,
      ...(gauche === undefined ? {} : { left: cmEnTwips(gauche) }),
      ...(droit === undefined ? {} : { right: cmEnTwips(droit) }),
      // Les deux s'excluent : celui qui n'est pas demandé n'est redonné que si l'autre ne l'est pas non plus,
      // et un suspendu déjà posé l'emporte sur une première ligne (c'est lui que lit le lecteur).
      ...(premiereLigne === undefined
        ? (hanging !== undefined ? { hanging } : firstLine !== undefined ? { firstLine } : {})
        : premiereLigne < 0 ? { hanging: cmEnTwips(-premiereLigne) } : { firstLine: cmEnTwips(premiereLigne) }),
    };
  }

  const [avant, apres, interligne] = [aEcrire('spaceBeforePt'), aEcrire('spaceAfterPt'), aEcrire('lineSpacing')];
  if (avant !== undefined || apres !== undefined || interligne !== undefined) {
    const { line, lineRule, ...autour } = paragraphe.aLaMain.espacement;
    ecritures.espacement = {
      ...autour,
      ...(avant === undefined ? {} : { before: ptEnVingtiemes(avant) }),
      ...(apres === undefined ? {} : { after: ptEnVingtiemes(apres) }),
      // L'interligne posé à la main est redonné avec sa règle — `auto` quand le fichier ne la porte pas.
      ...(interligne === undefined
        ? (line === undefined ? {} : { line, lineRule: lineRule ?? 'auto' })
        : { line: multipleEnInterligne(interligne), lineRule: 'auto' as const }),
    };
  }

  const aTracer = typeof demandes.border === 'string' ? cotesDeLaBordure(demandes.border) : undefined;
  if (aTracer !== undefined) {
    const cotes = ecrituresDeBordure(paragraphe, aTracer, typeof demandes.borderWidthPt === 'number' ? demandes.borderWidthPt : undefined);
    if (cotes.length > 0) ecritures.bordure = cotes;
  }

  if (demandes.shading !== undefined) {
    const dessous = paragraphe.fondSousLaMain;
    const fond = ecritureDeFond(demandes.shading, paragraphe.fond, paragraphe.formeDirecte.deParagraphe.includes('shd'), dessous.lisible && dessous.valeur === CANVAS_SHADING_NONE);
    if (fond !== undefined) ecritures.fond = fond;
  }

  // Une cellule que le lecteur ne sait pas nommer n'est pas écrite : l'exécuteur refuse l'appel avant d'en arriver ici.
  const { cellule } = paragraphe;
  if (demandes.cellShading !== undefined && cellule !== undefined && cellule.premierParagraphe !== null) {
    // Un fond posé à la main s'efface (C8) ; sans lui, un fond à retirer vient d'un style de tableau, et il est annulé à la main.
    // Ce qu'un style de tableau montrerait sous un fond effacé n'est pas lu : la relecture le dira non lisible.
    const fond = ecritureDeFond(demandes.cellShading, cellule.fond, cellule.fondALaMain, true);
    if (fond !== undefined) ecritures.fondDeCellule = { ...fond, cellule: `tc:${cellule.premierParagraphe}` };
  }

  const cadre = typeof demandes.cellBorder === 'string' ? cotesDeLaBordure(demandes.cellBorder) : undefined;
  if (cadre !== undefined && cellule !== undefined && cellule.premierParagraphe !== null) {
    const cotes: Partial<Record<CanvasParagraphBorderSide, TraitDeCase | null>> = {};
    for (const cote of CANVAS_PARAGRAPH_BORDER_SIDES) {
      const lu = cellule.cadre[cote];
      // Un côté qui ne se lit pas est écrit : rien ne prouve qu'il est dans l'état demandé.
      if (cadre.includes(cote)) {
        if (!(lu.lisible && lu.valeur > 0)) cotes[cote] = { lineStyle: 'single', lineWeightPt: ENCADRE_PAR_DEFAUT.epaisseurPt, color: ENCADRE_PAR_DEFAUT.couleur };
      } else if (!(lu.lisible && lu.valeur === 0)) {
        cotes[cote] = null;
      }
    }
    if (Object.keys(cotes).length > 0) ecritures.cadreDeCase = { cellule: `tc:${cellule.premierParagraphe}`, cotes };
  }
  return ecritures;
}

/**
 * Les réglages demandés, dans l'ordre du contrat partagé, sous la forme que
 * rend le lecteur de forme : un nombre au centième, l'unité la plus fine que la
 * lecture rende.
 */
export function reglagesDeParagrapheDemandes(source: Partial<Record<CanvasParagraphSettingKey, CanvasFormatSettingValue | undefined>>): ReglagesDeParagraphe {
  // Le style nommé en tête, comme dans le contrat partagé (`CANVAS_PARAGRAPH_SETTING_KEYS`).
  const reglages: ReglagesDeParagraphe = typeof source.styleId === 'string' ? { styleId: source.styleId } : {};
  for (const reglage of CANVAS_PARAGRAPH_FORMAT_KEYS) {
    const valeur = source[reglage];
    if (valeur !== undefined) reglages[reglage] = typeof valeur === 'number' ? Math.round(valeur * 100) / 100 : valeur;
  }
  for (const reglage of CANVAS_SHADING_SETTING_KEYS) {
    const valeur = source[reglage];
    const fond = typeof valeur === 'string' ? fondNormalise(valeur) : undefined;
    if (fond !== undefined) reglages[reglage] = fond;
  }
  const cadre = source[CANVAS_CELL_BORDER_SETTING_KEY];
  if (typeof cadre === 'string') reglages[CANVAS_CELL_BORDER_SETTING_KEY] = cadre;
  return reglages;
}

/**
 * La demande d'un appel : ses réglages normalisés et, quand un style nommé en
 * fait partie, ce style cherché UNE fois parmi ceux que le fichier définit.
 *
 * @returns La demande ; ou l'identifiant du style demandé que le fichier ne
 *   définit pas — l'appelant refuse alors avant toute écriture.
 */
export function demandeDeParagraphes(
  source: Partial<Record<CanvasParagraphSettingKey, CanvasFormatSettingValue | undefined>>,
  stylesDuFichier: readonly StyleDeParagraphe[],
): DemandeDeParagraphes | { styleAbsent: string } {
  const reglages = reglagesDeParagrapheDemandes(source);
  const { styleId } = reglages;
  if (styleId === undefined) return { reglages };
  const style = stylesDuFichier.find((candidat) => candidat.id === styleId);
  return style === undefined ? { styleAbsent: styleId } : { reglages, style };
}

/** Les réglages que porte chaque écriture par paragraphe. */
const REGLAGES_DE_LA_FAMILLE = {
  retraits: ['indentLeftCm', 'indentRightCm', 'indentFirstLineCm'],
  espacement: ['spaceBeforePt', 'spaceAfterPt', 'lineSpacing'],
} as const satisfies Record<FamilleQuiRemplace, readonly CanvasParagraphFormatKey[]>;

/** Une écriture qu'on ne fait pas : elle effacerait des attributs posés à la main (voir l'en-tête). */
export interface EcritureEmpechee {
  famille: FamilleQuiRemplace;
  /** Les réglages demandés que cette écriture aurait posés : ils restent tels quels sur ce paragraphe. */
  reglages: CanvasParagraphFormatKey[];
  /** Les attributs que l'éditeur ne saurait pas redonner. */
  attributs: string[];
}

/**
 * Parmi les écritures prévues pour ce paragraphe, celles qui effaceraient des
 * attributs posés à la main sur l'élément qu'elles réécrivent. Seules ces
 * écritures sont à écarter : le style, l'alignement, et l'autre famille si son
 * élément est sain, s'écrivent normalement.
 *
 * @param paragraphe La forme sur laquelle `ecritures` a été décidée
 *   ({@link ecrituresDuParagraphe}) : les réglages qui restent tels quels se
 *   jugent sur elle.
 */
export function ecrituresEmpechees(paragraphe: FormeDeParagraphe, ecritures: EcrituresDeParagraphe, demandes: ReglagesDeParagraphe): EcritureEmpechee[] {
  return (['retraits', 'espacement'] as const).flatMap((famille): EcritureEmpechee[] => {
    const attributs = paragraphe.aLaMain.nonRepris[famille];
    if (ecritures[famille] === undefined || attributs.length === 0) return [];
    const reglages = REGLAGES_DE_LA_FAMILLE[famille].filter((setting) => {
      const demande = demandes[setting];
      return demande !== undefined && jugerLeReglageDeParagraphe(setting, demande, paragraphe.paragraphe[setting]).issue !== 'conforme';
    });
    return [{ famille, reglages, attributs }];
  });
}

/**
 * Ce qu'un paragraphe reçoit pour une demande, et ce qui en est écarté :
 * `ecritures` ne contient pas les écritures de `empechees`.
 *
 * Le style nommé est à poser quand le paragraphe en porte un autre ; les
 * autres réglages se décident alors — écritures comme écritures écartées —
 * sur la MÊME forme, celle que le paragraphe montrera sous ce style
 * ({@link sousLeStyle}), calculée une fois.
 */
export function ecrituresDeLaDemande(lu: FormeDeParagraphe, { reglages, style }: DemandeDeParagraphes): { ecritures: EcrituresDeParagraphe; empechees: EcritureEmpechee[] } {
  const aPoser = style !== undefined && lu.styleId !== style.id ? style : undefined;
  const paragraphe = aPoser === undefined ? lu : sousLeStyle(lu, aPoser.forme);
  const ecritures: EcrituresDeParagraphe = { ...(aPoser === undefined ? {} : { style: aPoser.id }), ...ecrituresDuParagraphe(paragraphe, reglages) };
  const empechees = ecrituresEmpechees(paragraphe, ecritures, reglages);
  // Seule l'écriture qui abîmerait le paragraphe est écartée : son style, son alignement, et l'autre famille, s'écrivent.
  for (const { famille } of empechees) delete ecritures[famille];
  return { ecritures, empechees };
}
